import React, {useEffect, useRef, useState} from "react";
import {State, ViewStates} from "@common/components/view-states";
import {ViewState} from "@common/models/enums/viev-state.enum";
import {useArticleForm} from "../../hooks/use-article-form.hook";
import {SpecialistSearchResultDto} from "../../../core/models/interfaces/specialists-search-result-dto.interface";
import {SpecialistListResponse} from "../../../core/models/interfaces/specialist-list-response.interface";
import {SpecialistList, SpecialistSearchbar} from "../../../specialists";
import {useSpecialistApi} from "../../../core/hooks/use-specialist-api";
import SelectedArticleSpecialistTag from "../ui/selected-article-specialist-tag";
import AppButton from "@common/components/app-button";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {ReactComponent as PlusIcon} from "@assets/icons/plus.svg";
import SpecialistFormModal, {SpecialistFormModalData} from "../../../specialists/components/specialist-form-modal";
import {useLoading} from "../../../core/hooks/use-loading.hook";
import {LoadingKeys} from "../../../core/models/enums/loading-keys.enum";

const ArticleFormSpecialists = () => {
    const {form} = useArticleForm();
    const [results, setResults] = useState<SpecialistSearchResultDto[]>([]);
    const [query, setQuery] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const LIMIT = 20;
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const {getSearchSpecialists, error} = useSpecialistApi();
    const {showModal: showSpecialistFormModal} = useModalWithData<SpecialistFormModalData>(ModalIds.SPECIALIST_FORM);

    const selectedSpecialists = form.watch("specialists") as Array<{ id: string; fullName: string }>;
    const {isLoading} = useLoading();

    const handleEmitResults = (res: SpecialistListResponse, q: string) => {
        setResults(res?.specialists ?? []);
        setTotalCount(res?.totalCount ?? 0);
        setOffset((res?.specialists ?? []).length);
        setQuery(q);
    };

    const isSelected = (id: string) => selectedSpecialists?.some(s => s.id === id);

    const toggleSpecialist = (specialist: SpecialistSearchResultDto) => {
        const current = selectedSpecialists ?? [];
        if (current.some(s => s.id === specialist.id)) {
            form.setValue(
                "specialists",
                current.filter(s => s.id !== specialist.id),
                {shouldDirty: true, shouldValidate: true}
            );
        } else {
            form.setValue(
                "specialists",
                [...current, {id: specialist.id, fullName: specialist.fullName}],
                {shouldDirty: true, shouldValidate: true}
            );
        }
    };

    const hasMore = results.length < totalCount;
    const isInitialLoading = isLoading(LoadingKeys.USE_EXERCISE_API) && results.length === 0;

    const loadMore = async () => {
        if (!hasMore || isLoadingMore) return;
        try {
            setIsLoadingMore(true);
            const res = await getSearchSpecialists(query, LIMIT, offset);
            setResults(prev => [...prev, ...res.specialists]);
            setOffset(prev => prev + LIMIT);
        } catch (e) {
            console.error('Error loading more specialists', e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleCreateNewSpecialist = (): void => {
        showSpecialistFormModal({
            specialistId: undefined,
            onSuccess: loadInitialSpecialists
        });
    };

    const loadInitialSpecialists = async () => {
        try {
            const res = await getSearchSpecialists("", LIMIT, 0);
            setResults(res.specialists);
            setTotalCount(res.totalCount);
            setOffset(LIMIT);
        } catch (error) {
            console.error('Error loading specialists:', error);
        }
    };

    useEffect(() => {
       void loadInitialSpecialists();
    }, []);

    useEffect(() => {
        if (!sentinelRef.current) return;
        observerRef.current?.disconnect();
        observerRef.current = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                void loadMore();
            }
        }, {root: null, rootMargin: '200px', threshold: 0});
        observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [query, offset, totalCount, results.length]);

    return (
        <>
            <div className="flex flex-col gap-6 ">
                <h2 className="font-semibold text-2xl">Polecany specjalista</h2>
                <span className="font-semibold text-lg">Wybierz polecanych specjalistów, których dane kontaktowe będą wyświetlane pod artykułem.</span>
            </div>
            <div className="bg-white z-10 sticky top-0 flex flex-col gap-4 py-6 border-b border-outline-mild">
                <div className="flex gap-4 items-center flex-wrap">
                    <div className="flex gap-4 items-center w-full">
                        <SpecialistSearchbar
                            emitResults={handleEmitResults}
                            onQueryChange={setQuery}
                        />
                        <AppButton
                            onClick={handleCreateNewSpecialist}
                            className="bg-primary hover:bg-primary/80 w-fit rounded-[4rem] h-[40px] min-w-fit">
                            <div className="flex gap-2 text-red-400 justify-center items-center whitespace-nowrap">
                                <span className="font-semibold text-base text-on-primary ">Nowy specjalista</span>
                                <PlusIcon className="text-on-primary"/>
                            </div>
                        </AppButton>
                    </div>
                    <div className="flex flex-wrap">
                        {(selectedSpecialists ?? []).map(s => (
                            <SelectedArticleSpecialistTag key={s.id} id={s.id} fullName={s.fullName}/>
                        ))}
                    </div>
                </div>
            </div>
            <div className="pt-6">
                <ViewStates
                    loading={isInitialLoading}
                    error={error}
                    data={results}
                >
                    <State state={ViewState.LOADING}/>
                    <State state={ViewState.EMPTY}>
                        {query ? (
                            <span className="text-on-surface/60">Brak wyników dla „{query}”.</span>
                        ) : (
                            <span className="text-on-surface/60">Wpisz, aby wyszukać specjalistów.</span>
                        )}
                    </State>
                    <State state={ViewState.FETCHED}>
                        <SpecialistList
                            specialists={results}
                            showActionsCard={false}
                            onClick={toggleSpecialist as any}
                            isSpecialistSelected={isSelected}
                            isLoadingMore={isLoadingMore}
                        />
                        <div ref={sentinelRef}/>
                    </State>
                    <State state={ViewState.ERROR}/>
                </ViewStates>
            </div>

            <SpecialistFormModal/>
        </>
    )
}

export default ArticleFormSpecialists
